import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectUsersForNames } from '../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../store/userService';
import { convertBool } from '../../../utils/booleanUtils';
import { formatTimeString } from '../../../utils/dateFunctions';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../utils/formValidation/commonConstants';
import { AUDIT_EVENT_HISTORY_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../utils/sortFunctions';
import { uuid } from '../../../utils/stringUtils';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import AuditTimelineEventHistoryTableRow from './AuditTimelineEventHistoryTableRow';

const headCells = [
    { id: 'from', numeric: false, label: 'From', sortable: false },
    { id: 'to', numeric: false, label: 'To', sortable: false },
    { id: 'user', numeric: false, label: 'Author User Id' },
    { id: 'loggedDate', numeric: false, label: 'Date Changed' },
    { id: 'loggedTime', numeric: false, label: 'Time Changed' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'loggedDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const AuditTimelineEventHistoryManagement = ({ label }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const users = useSelector(selectUsersForNames);
    const auditEventHistory = useSelector(
        (state) => state.entities.participantService.participantTimelineEventsSearchData
    );
    const loadingParticipantTimelineEventsSearchData = useSelector(
        (state) => state.entities.participantService.loadingParticipantTimelineEventsSearchData
    );

    // USE EFFECTS
    useEffect(() => {
        if (!auditEventHistory?.length && !loadingParticipantTimelineEventsSearchData) {
            setLoadingContentMessage(AUDIT_EVENT_HISTORY_ERROR);
            return;
        }
        const userIds = [...new Set(auditEventHistory.map((el) => el.userId))];
        const missingUserIds = userIds
            .filter((el) => el !== 'Polaris System')
            .filter((el) => !users.find((entry) => entry.id === el))
            .filter((el) => el);
        if (missingUserIds.length > 0) dispatch(loadUsersMinimalDetails(missingUserIds));
        else {
            const updatedRows = auditEventHistory.map((el) => {
                const user = users.find((entry) => entry.id === el.userId);
                const history = el.history?.find((entry) => entry.field === label);
                return {
                    ...el,
                    id: uuid(),
                    user: user
                        ? `${user?.firstName ?? ''} ${user?.lastName ?? ''} (${user?.loginEmailAddress ?? ''})`
                        : 'Polaris System',
                    formattedTime: formatTimeString(el.loggedTime),
                    oldValue: convertBool(history?.oldValue),
                    newValue: convertBool(history?.newValue)
                };
            });
            setRows(updatedRows);
        }
    }, [auditEventHistory, users]);

    // HELPER FUNCTIONS
    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <AuditTimelineEventHistoryTableRow key={el.id} row={el} />
        ));
    };

    // RENDER
    return rows.length < 1 ? (
        <LoadingSpinner content={loadingContentMessage} />
    ) : (
        <ResultsTable
            defaultOrderBy={rowMetaData.orderBy}
            sortOrder={rowMetaData.order}
            headCells={headCells}
            passRowMetaDataUp={setRowMetaData}
            tableRows={createRows()}
            totalResults={rows.length}
        />
    );
};

AuditTimelineEventHistoryManagement.propTypes = {
    label: PropTypes.string
};

export default AuditTimelineEventHistoryManagement;
